/**
 * leaderboard-local.ts — W4-TOWER
 *
 * Renders a local top-10 leaderboard from localStorage towerHistory.
 * Columns: 最高樓層 / 時間 / 分數 / 日期
 */

import { loadProgress } from '../shared/progress';
import type { TowerRun } from '../shared/types';

export interface LeaderboardOptions {
  onBack: () => void;
}

function formatTime(totalSec: number): string {
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function formatDate(endedAt: number): string {
  const d = new Date(endedAt);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}/${mm}/${dd}`;
}

/** Return top 10 runs sorted by score desc. */
export function getTop10(history: TowerRun[]): TowerRun[] {
  return [...history]
    .sort((a, b) => b.score - a.score)
    .slice(0, 10);
}

export function renderLeaderboard(container: HTMLElement, options: LeaderboardOptions): void {
  const progress = loadProgress();
  const history: TowerRun[] = progress?.towerHistory ?? [];
  const top10 = getTop10(history);

  const root = document.createElement('div');
  root.className = 'leaderboard';
  root.style.cssText = `
    display: flex; flex-direction: column; align-items: center;
    padding: 32px 16px; gap: 16px; min-height: 100vh;
    background: #0a0a1a; color: #E0E0E0; font-family: sans-serif;
  `;

  // Title
  const title = document.createElement('h1');
  title.textContent = '🗼 魔王塔紀錄板';
  title.style.cssText = 'color: #FFD700; margin: 0; font-size: 28px;';
  root.appendChild(title);

  if (top10.length === 0) {
    const empty = document.createElement('p');
    empty.textContent = '尚無紀錄，快去挑戰！';
    empty.style.cssText = 'color: #78909C; font-size: 18px; margin-top: 32px;';
    root.appendChild(empty);
  } else {
    const table = document.createElement('table');
    table.style.cssText = `
      border-collapse: collapse; width: min(600px, 100%);
      background: #121212; border-radius: 8px; overflow: hidden;
    `;

    // Header
    const thead = document.createElement('thead');
    thead.innerHTML = `
      <tr style="background:#1e1e2e; color:#FFD700; text-align:center;">
        <th style="padding:12px 8px;">#</th>
        <th style="padding:12px 8px;">最高樓層</th>
        <th style="padding:12px 8px;">時間</th>
        <th style="padding:12px 8px;">分數</th>
        <th style="padding:12px 8px;">日期</th>
      </tr>
    `;
    table.appendChild(thead);

    // Rows
    const tbody = document.createElement('tbody');
    top10.forEach((run, idx) => {
      const tr = document.createElement('tr');
      const rowBg = idx % 2 === 0 ? '#1a1a2a' : '#161624';
      const rankColor = idx === 0 ? '#FFD700' : idx === 1 ? '#C0C0C0' : idx === 2 ? '#CD7F32' : '#E0E0E0';
      tr.style.cssText = `background:${rowBg}; text-align:center;`;
      tr.innerHTML = `
        <td style="padding:10px 8px; color:${rankColor}; font-weight:bold;">${idx + 1}</td>
        <td style="padding:10px 8px; font-size:20px; font-weight:bold;">${run.highestFloor} 層</td>
        <td style="padding:10px 8px;">${formatTime(run.totalSec)}</td>
        <td style="padding:10px 8px; color:#00E5FF; font-weight:bold;">${run.score.toLocaleString()}</td>
        <td style="padding:10px 8px; color:#78909C; font-size:14px;">${formatDate(run.endedAt)}</td>
      `;
      tbody.appendChild(tr);
    });
    table.appendChild(tbody);
    root.appendChild(table);
  }

  // Back button
  const backBtn = document.createElement('button');
  backBtn.textContent = '← 返回';
  backBtn.style.cssText = `
    margin-top: 16px; padding: 12px 32px; font-size: 18px;
    background: #1e1e2e; color: #E0E0E0; border: 1px solid #37474F;
    border-radius: 8px; cursor: pointer;
  `;
  backBtn.addEventListener('click', () => {
    root.remove();
    options.onBack();
  });
  root.appendChild(backBtn);

  container.appendChild(root);
}
